import { useEffect, useRef, useState } from 'react';
import { readFocusedTaskActionResult, readWorkHistory, type WorkHistoryCursor } from '@/lib/work-history';
import { Link, useLocation } from 'react-router-dom';
import type { ActivitySubjectThreadItem } from '@shared/types/activity-feed';
import { ViewRouter } from '@/components/views/ViewRouter';
import { WorkActionsProvider } from '@/components/work/WorkActionsContext';
import { Button } from '@/components/ui/Button';
import { formatAbsoluteDateTime } from './activity-ui';
import { AccountFlowResult } from './AccountFlowResult';
import { accountListFilters } from '@/lib/account-directory';

/** Where a past item's result is read: its run, or its anchor on the account page. */
export const resultPath = (item: Pick<ActivitySubjectThreadItem, 'sourceRunId' | 'taskActionRunId' | 'toolKey'>) => item.sourceRunId && !item.taskActionRunId
  ? item.toolKey ? `/tools/${encodeURIComponent(item.toolKey)}?run=${encodeURIComponent(item.sourceRunId)}` : `/executions/${encodeURIComponent(item.sourceRunId)}`
  : null;
const readableToolLabel = (value: string | null) => value && !/^[a-z0-9_-]+\.[a-z0-9_.-]+$/i.test(value) && !/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(value) ? value : null;

export const ThreadCurrentResult = ({
  item,
  focused = false,
  subjectId,
}: {
  item: ActivitySubjectThreadItem;
  focused?: boolean;
  subjectId?: string;
}) => {
  const location = useLocation();
  const article = useRef<HTMLElement>(null);
  const legacyAnchorFocused = Boolean(
    item.taskActionRunId && location.hash === `#task-action-${item.taskActionRunId}`
  );
  const isFocused = focused || legacyAnchorFocused;
  const [expanded, setExpanded] = useState(isFocused);
  useEffect(() => {
    if (!isFocused) return;
    setExpanded(true);
    if (!legacyAnchorFocused) return;
    const frame = requestAnimationFrame(() => article.current?.scrollIntoView({ block: 'start' }));
    return () => cancelAnimationFrame(frame);
  }, [isFocused, item.id, legacyAnchorFocused]);
  const [documentTitle, setDocumentTitle] = useState(item.title);
  const href = subjectId && item.sourceRunId && !item.taskActionRunId
    ? `/threads/${encodeURIComponent(subjectId)}?flow=${encodeURIComponent(item.sourceRunId)}${accountListFilters(new URLSearchParams(location.search)).replace(/^\?/, '&')}`
    : resultPath(item);
  const tool = readableToolLabel(item.toolLabel);
  const isNote = !item.resultView && item.title.length > 160;
  const title = isNote ? 'Note de suivi' : documentTitle;
  const document = item.resultView?.view_type === 'artifact_document' ? item.resultView.data : null;
  const bodyHasTitle = document && /^#\s+\S/m.test(document.body);
  const summary = document ? null : isNote ? item.title : item.summary && !item.title.startsWith(item.summary.replace(/…$/, '')) ? item.summary : null;
  const summaryNeedsDisclosure = Boolean(summary && summary.length > 140);
  return <article ref={article} id={item.taskActionRunId ? `task-action-${item.taskActionRunId}` : `activity-${item.id}`} className="scroll-mt-24 border-t border-domino-cardBorder py-5">
    <div className="mb-2 flex flex-wrap gap-3 text-xs text-domino-inkSoft">
      {item.eventAt && <time dateTime={item.eventAt}>{formatAbsoluteDateTime(item.eventAt)}</time>}
      {tool && tool !== item.title && <span>{tool}</span>}
      {item.actorLabel && !/^[0-9a-f-]{36}$/i.test(item.actorLabel) && <span>{item.actorLabel}</span>}
    </div>
    {!(expanded && bodyHasTitle) && <h3 className="text-base font-semibold text-domino-ink">{title}</h3>}
    {summary && summary !== title && !summaryNeedsDisclosure && <p className="mt-2 text-sm leading-6 text-domino-inkSoft">{summary}</p>}
    {summary && summary !== title && summaryNeedsDisclosure && <details className="group mt-2">
      <summary className="inline-flex cursor-pointer list-none items-center py-1 text-sm font-medium text-domino-aubergine">Voir le résumé</summary>
      <p className="mt-2 max-w-3xl text-sm leading-6 text-domino-inkSoft">{summary}</p>
    </details>}
    {item.resultView && <details open={expanded} onToggle={event => setExpanded(event.currentTarget.open)} className="mt-3">
      <summary className="cursor-pointer py-2 text-sm font-medium text-domino-aubergine">{item.resultView.view_type === 'artifact_document' ? 'Consulter le document' : 'Consulter le résultat'}</summary>
      <div className="pt-3"><WorkActionsProvider onResultTitleChange={setDocumentTitle} actions={[]} historical resultSource={item.resultView?.view_type === 'artifact_document' && (item.taskActionRunId || item.sourceRunId) ? { kind: item.taskActionRunId ? 'task_action' : 'flow', id: (item.taskActionRunId ?? item.sourceRunId)! } : undefined}><ViewRouter payload={item.resultView} hideTitle /></WorkActionsProvider></div>
    </details>}
    {href && href !== `${location.pathname}${location.search}` && <Link to={href} className="mt-3 inline-block text-sm font-medium text-domino-aubergine underline underline-offset-4">Ouvrir {tool ?? item.title}{tool && item.eventAt ? ` du ${new Date(item.eventAt).toLocaleDateString('fr-FR')}` : ''}</Link>}
  </article>;
};

export const ThreadTimeline = ({
  items,
  subjectId,
  subjectLabel,
  focusedTaskActionRunId = null,
  focusedFlowRunId = null,
  onContinueFocused,
}: {
  items: ActivitySubjectThreadItem[];
  subjectId?: string;
  subjectLabel?: string;
  focusedTaskActionRunId?: string | null;
  focusedFlowRunId?: string | null;
  onContinueFocused?: () => void;
}) => {
  const [history, setHistory] = useState<ActivitySubjectThreadItem[] | null>(null);
  const [cursor, setCursor] = useState<WorkHistoryCursor | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focusedResult, setFocusedResult] = useState<{ subjectId: string; runId: string; item: ActivitySubjectThreadItem } | null>(null);
  const [focusLoading, setFocusLoading] = useState(false);
  const [focusError, setFocusError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!subjectId) return;
    let alive = true;
    setLoading(true); setError(null);
    void readWorkHistory(subjectId,null).then(page => { if (alive) { setHistory(page.items); setCursor(page.nextCursor); } })
      .catch(cause => { if (alive) setError(cause.message); }).finally(() => { if (alive) setLoading(false); });
    return () => {alive = false;};
  }, [subjectId,items,reload]);
  const loadMore = async () => {
    if (!subjectId || !cursor || loading) return;
    setLoading(true); setError(null);
    try { const page = await readWorkHistory(subjectId,cursor); setHistory(previous => [...(previous ?? []),...page.items]); setCursor(page.nextCursor); }
    catch { setError('Les événements précédents n’ont pas pu être chargés.'); }
    finally { setLoading(false); }
  };
  const [limit, setLimit] = useState(8);
  const location = useLocation();
  const ordered = [...new Map((history ?? items).map(item => [item.taskActionRunId ? `task:${item.taskActionRunId}` : item.kind === 'run' && item.sourceRunId ? `run:${item.sourceRunId}` : item.id, item])).values()]
    .sort((a, b) => (b.eventAt ?? '').localeCompare(a.eventAt ?? '') || b.id.localeCompare(a.id));
  const pagedFocusedItem = focusedTaskActionRunId
    ? ordered.find((item) => item.taskActionRunId === focusedTaskActionRunId) ?? null : null;
  useEffect(() => {
    setFocusedResult(null); setFocusError(null);
    if (!subjectId || !focusedTaskActionRunId || history === null || pagedFocusedItem) return;
    let alive = true;
    setFocusLoading(true);
    void readFocusedTaskActionResult(subjectId, focusedTaskActionRunId)
      .then(result => { if (alive && result) setFocusedResult({ subjectId, runId: focusedTaskActionRunId, item: result }); })
      .catch(() => { if (alive) setFocusError('Le résultat demandé n’a pas pu être chargé.'); })
      .finally(() => { if (alive) setFocusLoading(false); });
    return () => { alive = false; };
  }, [subjectId, focusedTaskActionRunId, history, Boolean(pagedFocusedItem)]);
  const focusedItem = pagedFocusedItem ?? (focusedResult?.subjectId === subjectId && focusedResult?.runId === focusedTaskActionRunId ? focusedResult.item : null);
  if (focusedFlowRunId && subjectId) {
    return <>
      <AccountFlowResult subjectId={subjectId} subjectLabel={subjectLabel} runId={focusedFlowRunId} />
      <details className="mt-6 border-t border-domino-cardBorder pt-4">
        <summary className="cursor-pointer list-none text-base font-semibold text-domino-ink">Activité du compte</summary>
        <div className="mt-3">{ordered.filter(item => item.sourceRunId !== focusedFlowRunId).slice(0, limit).map(item => <ThreadCurrentResult key={item.id} item={item} subjectId={subjectId} />)}</div>
        {error && <p role="alert" className="py-3 text-sm text-red-700">{error}</p>}
        {cursor && <Button variant="secondary" disabled={loading} onClick={() => void loadMore()}>Charger les événements précédents</Button>}
      </details>
    </>;
  }
  if (focusedItem) {
    const remainingItems = ordered.filter((item) => item.id !== focusedItem.id);
    return <section aria-labelledby="focused-work-result-title" className="mt-8">
      <div className="rounded-2xl border border-domino-cardBorder bg-domino-white px-5 py-5 md:px-6">
        <h2 id="focused-work-result-title" className="text-lg font-semibold text-domino-ink">Résultat du travail</h2>
        <p className="mt-1 max-w-3xl text-sm leading-6 text-domino-inkSoft">
          Examinez les options, puis dites à Domino dans le chat celle que vous retenez ou ce que vous souhaitez approfondir.
        </p>
        <ThreadCurrentResult item={focusedItem} focused subjectId={subjectId} />
        {onContinueFocused && <Button variant="primary" onClick={onContinueFocused}>Poursuivre dans le chat</Button>}
      </div>
      <details className="group mt-6 border-t border-domino-cardBorder pt-4">
        <summary className="cursor-pointer list-none text-base font-semibold text-domino-ink">
          Activité du compte <span className="ml-2 text-sm font-normal text-domino-inkSoft">{remainingItems.length}</span>
        </summary>
        <div className="mt-3">
          {remainingItems.slice(0, limit).map((item) => <ThreadCurrentResult key={item.id} item={item} subjectId={subjectId} />)}
          {error && <div role="alert" className="py-3 text-sm text-red-700">{error}<Button variant="secondary" onClick={() => setReload(v => v + 1)}>Réessayer</Button></div>}
          {cursor && remainingItems.length <= limit && <Button variant="secondary" disabled={loading} onClick={() => void loadMore()}>Charger les événements précédents</Button>}
          {remainingItems.length > limit && <Button variant="secondary" onClick={() => setLimit(value => value + 8)}>Afficher les événements précédents</Button>}
        </div>
      </details>
    </section>;
  }
  if (focusedTaskActionRunId && subjectId && (history === null || focusLoading || focusError || !focusedItem)) {
    return <section aria-label="Résultat du travail" className="mt-8 py-4 text-sm text-domino-inkSoft">
      {focusError ? <p role="alert">{focusError}</p> : history === null || focusLoading ? <p role="status">Chargement du résultat…</p> : <p>Résultat introuvable ou inaccessible.</p>}
    </section>;
  }
  return <section aria-label="Activité du compte" className="mt-8">
    <h2 className="mb-3 text-lg font-semibold text-domino-ink">Activité du compte</h2>
    {!ordered.length && <p className="py-4 text-sm text-domino-inkSoft">Aucun travail encore rattaché à ce compte.</p>}
    {ordered.slice(0, Math.max(limit, ordered.findIndex(item => item.taskActionRunId && location.hash === `#task-action-${item.taskActionRunId}`) + 1)).map(item => <ThreadCurrentResult key={item.id} item={item} subjectId={subjectId} />)}
    {error && <div role="alert" className="py-3 text-sm text-red-700">{error}<Button variant="secondary" onClick={() => setReload(v => v + 1)}>Réessayer</Button></div>}
    {cursor && ordered.length <= limit && <Button variant="secondary" disabled={loading} onClick={() => void loadMore()}>Charger les événements précédents</Button>}
    {ordered.length > limit && <Button variant="secondary" onClick={() => setLimit(value => value + 8)}>Afficher les événements précédents</Button>}
  </section>;
};
